import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
} from "react";
import {
  MdClose,
  MdInfo,
  MdCheckCircle,
  MdKeyboardArrowDown,
  MdWarning,
  MdReport,
} from "react-icons/md";
import styles from "./Toast.module.scss";
import { humanizeToastMessage } from "./toastNormalizer";

const typeOptions = {
  success: MdCheckCircle,
  warning: MdWarning,
  danger: MdReport,
  info: MdInfo,
};

const Toast = ({
  title,
  description,
  appearance = "success",
  icon,
  actions,
  isExpanded = true,
  autoDismiss = true,
  duration = 4500,
  showClose = true,
  onDismiss,
  onDismissed,
  onClose,
  ...props
}) => {
  const [displayContent, setDisplayContent] = useState(isExpanded);
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const toastRef = useRef(null);
  const timerRef = useRef(null);

  const handleClose = useCallback(
    (event) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setIsClosing(true);
      setTimeout(() => {
        setIsVisible(false);
        if (typeof onDismiss === "function") onDismiss();
        if (typeof onDismissed === "function") onDismissed();
        if (typeof onClose === "function") onClose();
        if (typeof props?.onDismiss === "function") props.onDismiss();
        if (typeof props?.onDismissed === "function") props.onDismissed();
        if (typeof props?.onClose === "function") props.onClose();
      }, 200);
    },
    [onDismiss, onDismissed, onClose, props]
  );

  // Auto-dismiss after specified duration
  useEffect(() => {
    if (autoDismiss) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, duration);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
  }, [autoDismiss, duration, handleClose]);

  // Pause timer on hover, resume on leave
  const handleMouseEnter = useCallback(() => {
    if (autoDismiss && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [autoDismiss]);

  const handleMouseLeave = useCallback(() => {
    if (autoDismiss && !timerRef.current) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 2500);
    }
  }, [autoDismiss, handleClose]);

  // Normalize and humanize title and description
  const { heading: formattedTitle, text: formattedDescription } = humanizeToastMessage(
    title,
    description,
    appearance
  );

  if (!isVisible) return null;

  // Get the correct icon component
  const IconComponent = icon || typeOptions[appearance] || typeOptions.info;
  const hasDescription = Boolean(
    formattedDescription && formattedDescription !== formattedTitle
  );
  const hasExpandableContent = hasDescription || Boolean(actions && actions.length > 0);

  return (
    <div
      className={`${styles["toast-container__toast"]} ${styles[appearance || "default"]
        } ${isClosing ? styles["is-closing"] : ""}`}
      ref={toastRef}
      toast-expanded={displayContent?.toString()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-start" style={{ flex: 1, minWidth: 0 }}>
          <span className={styles["toast-container__toast--icon-title"]}>
            {React.createElement(IconComponent, {
              className: appearance === "info" ? styles["info-icon"] : "",
            })}
          </span>
          <span
            className={styles["toast-container__toast--title"]}
            style={{ wordBreak: "break-word" }}
          >
            {formattedTitle}
          </span>
        </div>

        <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
          {hasExpandableContent && !isExpanded && (
            <div
              className={`${styles["toast-container__toast--expand"]}`}
              onClick={() => setDisplayContent(!displayContent)}
              title={displayContent ? "Daralt" : "Genişlet"}
              role="button"
            >
              <MdKeyboardArrowDown
                className={
                  displayContent ? styles["toast-container__toast--rotate"] : ""
                }
              />
            </div>
          )}
          {showClose && (
            <button
              type="button"
              className={`${styles["toast-container__toast--close"]}`}
              onClick={handleClose}
              title="Kapat"
              aria-label="Bildirimi kapat"
            >
              <MdClose />
            </button>
          )}
        </div>
      </div>

      {hasExpandableContent && (
        <div
          className={`${styles["toast-content"]} ${displayContent || isExpanded ? styles["toast-content--visible"] : ""
            }`}
        >
          {hasDescription && (
            <div className={styles["toast-content__description"]}>
              {formattedDescription}
            </div>
          )}
          {actions && actions.length > 0 && (
            <div
              className={styles["toast-content__buttons"]}
              style={{
                marginLeft: ["default", "link"].includes(appearance)
                  ? "20px"
                  : "33px",
              }}
            >
              {actions?.map((action, index) => (
                <div
                  key={index}
                  className={`${styles["toast-content__buttons--button"]} ${styles[`toast-content__buttons--${appearance || "default"}`]
                    }`}
                >
                  <div onClick={action?.onClick}>{action.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ToastContainer = ({
  autoDismiss = true,
  onDismissed,
  children,
  isExpanded = true,
}) => {
  const toastContainerRef = useRef(null);
  const [totalChildren, setTotalChildren] = useState(0);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    setToasts(children);
    setTotalChildren(children?.length || (Array.isArray(children) ? children.length : 1));
  }, [children]);

  return (
    <div
      className={styles["toast-container"]}
      ref={toastContainerRef}
      style={{
        ...(totalChildren === 0 && { display: "none" }),
      }}
    >
      {React.Children.toArray(toasts)
        ?.slice()
        ?.reverse()
        ?.map((toast, index) => {
          const key = toast.key || index;
          return (
            <Children
              key={key}
              index={index}
              onDismissed={onDismissed}
              autoDismiss={autoDismiss}
              isExpanded={isExpanded}
            >
              {toast}
            </Children>
          );
        })}
    </div>
  );
};

const Children = forwardRef(
  ({ children, onDismissed, autoDismiss, index }, ref) => {
    return (
      <div ref={ref} className={styles["children-container"]}>
        {React.isValidElement(children)
          ? React.cloneElement(children, {
            ...(!children.props.onDismissed &&
              !children.props.onClose &&
              !children.props.onDismiss &&
              onDismissed
              ? { onDismissed }
              : {}),
            ...(children.props.autoDismiss === undefined && autoDismiss !== undefined
              ? { autoDismiss }
              : {}),
            firsToast: index === 0,
          })
          : children}
      </div>
    );
  }
);

export { Toast, ToastContainer, humanizeToastMessage };
